/**
 * Pure helpers for rolling weighted coverage up to a state total and for
 * mapping a 0..1 coverage fraction onto a choropleth bucket/colour.
 *
 * Parcel coverage weights each county by population; permit coverage weights
 * each jurisdiction by population too (Shovels-style: % of people living
 * somewhere we hold permit data). The maths is the same: sum the weight of
 * the covered units, divide by the weight of all units.
 */
/** Sum covered vs total weight across units and derive the coverage fraction. */
export function rollupCoverage(units) {
    let coveredWeight = 0;
    let totalWeight = 0;
    let coveredCount = 0;
    for (const u of units) {
        const w = u.weight > 0 ? u.weight : 0;
        totalWeight += w;
        if (u.covered) {
            coveredWeight += w;
            coveredCount += 1;
        }
    }
    return {
        coveredWeight,
        totalWeight,
        coverage: totalWeight > 0 ? coveredWeight / totalWeight : 0,
        coveredCount,
        totalCount: units.length,
    };
}
/** National parcel coverage: population-weighted across states, with parcel count. */
export function nationalParcelCoverage(states) {
    let cw = 0;
    let tw = 0;
    let rc = 0;
    for (const s of states) {
        cw += s.covered_population;
        tw += s.total_population;
        rc += s.parcel_count;
    }
    return { coverage: tw > 0 ? cw / tw : 0, record_count: rc, covered_weight: cw, total_weight: tw };
}
/** National permit coverage: population-weighted across states, with permit count. */
export function nationalPermitCoverage(states) {
    let cw = 0;
    let tw = 0;
    let rc = 0;
    for (const s of states) {
        cw += s.covered_population;
        tw += s.total_population;
        rc += s.permit_count;
    }
    return { coverage: tw > 0 ? cw / tw : 0, record_count: rc, covered_weight: cw, total_weight: tw };
}
/** Roll per-state fresh + located counts up nationally; null for a snapshot
 *  written before those fields shipped. */
export function permitActivity(states) {
    if (!states.some((s) => s.permits_last_30d != null || s.located_count != null))
        return null;
    let fresh = 0;
    let located = 0;
    let total = 0;
    for (const s of states) {
        fresh += s.permits_last_30d ?? 0;
        located += s.located_count ?? 0;
        total += s.permit_count;
    }
    return {
        permits_last_30d: fresh,
        located_count: located,
        located_share: total > 0 ? located / total : 0,
    };
}
/**
 * Bucket a 0..1 coverage fraction. Boundaries are inclusive on the low side:
 * exactly 0 → none, (0, .25] → low, (.25, .6] → medium, (.6, .99) → high,
 * ≥ .99 → full. Values are clamped to [0, 1] first.
 */
export function coverageBucket(coverage) {
    const c = clamp01(coverage);
    if (c <= 0)
        return 'none';
    if (c < 0.25)
        return 'low';
    if (c < 0.6)
        return 'medium';
    if (c < 0.99)
        return 'high';
    return 'full';
}
const BUCKET_COLORS = {
    // Light grey → teal ramp (matches the app's primary #1f7a8c).
    none: '#e2e8f0',
    low: '#b8dbd9',
    medium: '#7fb7bd',
    high: '#3f8f9b',
    full: '#1f7a8c',
};
/** Choropleth fill colour for a coverage fraction. */
export function coverageColor(coverage) {
    return BUCKET_COLORS[coverageBucket(coverage)];
}
function clamp01(n) {
    if (Number.isNaN(n))
        return 0;
    if (n < 0)
        return 0;
    if (n > 1)
        return 1;
    return n;
}
//# sourceMappingURL=rollup.js.map